import { useState } from 'react'
import { tournamentApi } from '../services/api'

/**
 * Owner approval panel for an over-limit refund (guardrail #236). Shown on a
 * trip (S&P booking detail / CTQ builder) when a refund is parked in
 * 'awaiting_approval': a staffer set it up above their refund limit, so the
 * customer hasn't been charged-back yet and nothing has started.
 *
 * The account owner sees Approve / Deny. A non-owner (e.g. the staffer who set
 * it up) sees a read-only "waiting for approval" note. Approve/Deny hit the
 * request-keyed endpoints, so this works for both booking + quote refunds.
 *
 * Props:
 *   pending    the pendingRefund object (status === 'awaiting_approval')
 *   canApprove is the current user the owner (may act)
 *   onDone     () -> reload the trip after a decision
 *   t          translation fn
 */
function fmtMoney(v, cur) {
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const s = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return cur ? `${cur} ${s}` : s
}

export default function RefundApprovalPanel({ pending, canApprove, onDone, t }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  if (!pending || pending.status !== 'awaiting_approval') return null

  const cur = pending.currency
  const amountStr = fmtMoney(pending.amount, cur)
  const who = pending.initiatedByName || t('pr_approval_who_fallback', 'A team member')

  async function decide(kind) {
    if (kind === 'deny' && !window.confirm(t('pr_approval_deny_confirm', 'Deny this {amount} refund? It will be cancelled and {who} notified.', { amount: amountStr, who }))) return
    if (kind === 'approve' && !window.confirm(t('pr_approval_approve_confirm', 'Approve this {amount} refund? It will start processing and the customer will be refunded once the cancellations clear.', { amount: amountStr }))) return
    setBusy(true); setErr(null)
    try {
      await tournamentApi.post(`/portal/reseller/refund-requests/${pending.id}/${kind}`, {})
      window.alert(kind === 'approve'
        ? t('pr_approval_approved_done', 'Approved — the refund is now processing.')
        : t('pr_approval_denied_done', 'Refund denied.'))
      onDone && onDone()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ marginBottom: 12, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>⏳ {t('pr_approval_heading', 'Refund awaiting your approval')} · {amountStr}</span>
        {canApprove && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => decide('deny')} disabled={busy}
              style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? t('pr_approval_working', 'Working…') : t('pr_approval_deny', 'Deny')}
            </button>
            <button onClick={() => decide('approve')} disabled={busy}
              style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, border: 'none', background: '#166534', color: '#fff', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? t('pr_approval_working', 'Working…') : t('pr_approval_approve', 'Approve & start refund')}
            </button>
          </div>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: '#92400e', marginTop: 4 }}>
        {canApprove
          ? t('pr_approval_body', '{who} set up a {amount} refund, above their {limit} limit.', { who, amount: amountStr, limit: pending.initiatedByLimit != null ? fmtMoney(pending.initiatedByLimit, cur) : '—' })
          : t('pr_approval_waiting_staff', "This refund is above your limit — it's waiting for the account owner to approve it before it's issued.")}
      </div>
      {err && <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c' }}>{err}</div>}
    </div>
  )
}
